import { publicInstructorSchema } from "@nyntax/contracts/schemas/course";
import type { PublicInstructor } from "@nyntax/contracts/schemas/course";

import { readJson } from "./read-json";

/** Raised when an instructor page cannot be loaded, for reasons other than the instructor not existing. */
export class InstructorLoadError extends Error {
  /**
   * @param message - What went wrong, shown to the operator in logs.
   * @param options - Optional `cause`, usually the underlying network error.
   */
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = "InstructorLoadError";
  }
}

interface FetchInstructorOptions {
  baseUrl: string;
  fetcher?: typeof fetch;
  id: string;
}

/**
 * Loads an instructor's public page. Returns null only when the API says there
 * is no such instructor (404): an outage must never look like "not found".
 *
 * @param options - The API base URL, the instructor id, and an optional fetch implementation.
 * @returns The validated instructor page, or null when the API answers 404.
 */
export const fetchInstructor = async ({
  baseUrl,
  fetcher = fetch,
  id,
}: FetchInstructorOptions): Promise<PublicInstructor | null> => {
  let response: Response;

  try {
    response = await fetcher(
      `${baseUrl}/v1/instructors/${encodeURIComponent(id)}`,
      {
        headers: { accept: "application/json" },
        cache: "no-store",
      }
    );
  } catch (error) {
    throw new InstructorLoadError("The instructor API is unreachable", {
      cause: error,
    });
  }

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new InstructorLoadError(`The instructor API answered ${response.status}`);
  }

  const parsed = publicInstructorSchema.safeParse(await readJson(response));

  if (!parsed.success) {
    throw new InstructorLoadError("The instructor API returned an unexpected body");
  }

  return parsed.data;
};

/**
 * The address of an instructor's public page.
 *
 * @param locale - The active locale segment.
 * @param id - The instructor's Publisher id.
 * @returns A path such as `/en-US/instructors/org_123`.
 */
export const instructorPath = (locale: string, id: string): string =>
  `/${locale}/instructors/${encodeURIComponent(id)}`;
